import { ViewController, MatchdayPickEvent, ModelUpdateEvent, TimeoutEvent, DataReceiveEvent, SelectionUpdateEvent,
         Matchday, Phase } from "@gaming/srvg-classes-core";
import { MatchdayArea } from "../../View/MatchdayArea";
import { CallToActionViewController } from "./CallToActionViewController";
import { OddsViewController } from "./OddsViewController";
import { OddsFrameViewController } from "./OddsFrameViewController";
import { MatchdaypickerController } from "./MatchdaypickerController";
import { PhaseChangeEvent } from "../../Event/PhaseChangeEvent";

export class MatchdayAreaController extends ViewController {
    matchdayArea: MatchdayArea;

    calltoactionViewController: CallToActionViewController;
    oddsViewController: OddsViewController;
    oddsFrameViewController: OddsFrameViewController;
    matchdaypickerController: MatchdaypickerController;
    isOnAutopilot:boolean;

    constructor( options ) {
        options = options || {};
        options.view = options.matchdayArea || null;
        super( options );
        this.matchdayArea = options.matchdayArea || null;

        this.name = 'matchdayAreaController';

        // TODO idea: remove isOnAutopilot and let matchday area just look at selected day
        // if selected day is current or past, continue as if on autopilot, else, don't change the selected day
        this.isOnAutopilot = true;

        /******************* initialize controllers ************************/

        this.matchdaypickerController = null;
        if( this.matchdayArea.matchdayPicker ) {
            this.matchdaypickerController = this.addChildViewController( new MatchdaypickerController( {
                matchdayPicker: this.matchdayArea.matchdayPicker,
                matchdayAreaModel: this.matchdayArea.matchdayAreaModel
            } ) );
        }

        this.calltoactionViewController = this.addChildViewController( new CallToActionViewController( {
            callToActionView: this.matchdayArea.calltoactionView,
            matchdayAreaModel: this.matchdayArea.matchdayAreaModel
        }) );
        if( this.matchdayArea.oddsGallery ) {
            this.oddsViewController = this.addChildViewController( new OddsViewController( {
                oddsView: this.matchdayArea.oddsGallery,
                pagerView: this.matchdayArea.oddsPager,
                betmarket: 'win_match_including_overtime'
            }) );
        }
        else {
            this.oddsViewController = null;
        }

        if( this.matchdayArea.oddsFrame ) {
            this.oddsFrameViewController = this.addChildViewController( new OddsFrameViewController( {
                oddsFrame: this.matchdayArea.oddsFrame,
                channel: srvg.videooptions.defaultchannel || 0
            } ) );
        }
        else {
            this.oddsFrameViewController = null;
        }

        /******************* connect components ************************/
        if( this.matchdaypickerController ) {
            app.customerIntegration.addSubscriberForEvent( this, MatchdayPickEvent );
            this.matchdaypickerController.addSubscriberForEvent( this, MatchdayPickEvent );
            this.matchdaypickerController.addSubscriberForEvent( this, PhaseChangeEvent );
        }
        app.timelineController.addSubscriberForEvent( this, PhaseChangeEvent );

        this.matchdayArea.matchdayAreaModel.addSubscriberForEvent( this.calltoactionViewController, ModelUpdateEvent );

        if( app.inactivityController ) {
            app.inactivityController.addSubscriberForEvent( this.calltoactionViewController, TimeoutEvent );
        }

        if( this.oddsViewController ) {
            if( app.inactivityController ) {
                app.inactivityController.addSubscriberForEvent( this.oddsViewController, TimeoutEvent );
            }
            this.matchdayArea.matchdayAreaModel.addSubscriberForEvent( this.oddsViewController, ModelUpdateEvent );
            this.matchdayArea.calltoactionView.clockView.addSubscriberForEvent( this.oddsViewController, TimeoutEvent );
        }

        if( this.oddsFrameViewController ) {
            this.matchdayArea.matchdayAreaModel.addSubscriberForEvent( this.oddsFrameViewController, ModelUpdateEvent );
            this.oddsViewController.oddsView.addSubscriberForEvent( this.oddsFrameViewController, SelectionUpdateEvent )
            app.matchIdsFeedController.addSubscriberForEvent( this, DataReceiveEvent );
        }
    }

    private setSelectedMatchday( matchday, reason?:string ) {
        this.matchdayArea.matchdayAreaModel.setSelectedMatchday( matchday, reason );

        if( this.matchdaypickerController ) {
            this.matchdaypickerController.matchdayPicker.setMatchday( matchday );
        }

        if( this.oddsViewController ) {
            // clear odds view
            this.oddsViewController.oddsView.stopAnimation();
            this.oddsViewController.oddsView.setItems([]);
            if (this.oddsViewController.pagerView) {
                this.oddsViewController.pagerView.setLength(0);
            }
        }
    }

    // called by timeline controller
    handlePhaseChangeEvent( event:PhaseChangeEvent, provider ) {
        var matchdayAreaModel = this.matchdayArea.matchdayAreaModel;

        // update current matchday in model
        matchdayAreaModel.currentMatchday = event.matchday;
        matchdayAreaModel.currentPhase = event.phase;

        if( this.matchdaypickerController ) {
            if( !this.isOnAutopilot && this.shouldSwitchToAutopilot(  event.matchday, event.phase ) ) {
                this.isOnAutopilot = true;
            }
            this.updateMatchdaypickerSelectedMatchday( event.matchday, event.phase );
        }
        if( this.isOnAutopilot ) {
            this.updateAutoSelectedMatchday( event.matchday, event.phase );
        }
    }

    private updateAutoSelectedMatchday( currentMatchday: Matchday, currentPhase: Phase ) {
        // calculate selected matchday
        var matchday = currentMatchday;

        // betstop passed?
        if( currentPhase.shouldSwitchToNextMatchday() ) {
            if( matchday.next ) {
                matchday = <Matchday>matchday.next;
            }
        }
        // adjust for offset of this matchday area
        for( var i = 0; i < this.matchdayArea.matchdayAreaModel.offset; i++ ) {
            if( matchday.next ) {
                matchday = <Matchday>matchday.next;
            }
        }

        // update selected matchday in model
        if( ! matchday.isEqualTo( this.matchdayArea.matchdayAreaModel.selectedMatchday ) ) {
            this.setSelectedMatchday( matchday );
        }
    }

    private shouldSwitchToAutopilot( currentMatchday: Matchday, currentPhase: Phase ): boolean {
        var matchdayAreaModel = this.matchdayArea.matchdayAreaModel;

        // if not on autopilot, but selected matchday is either the current matchday or in the past, and the betstop phase just started, turn autopilot on
        if( !this.isOnAutopilot
            && matchdayAreaModel.selectedMatchday
            && matchdayAreaModel.selectedMatchday.start <= matchdayAreaModel.currentMatchday.start
            && matchdayAreaModel.currentPhase.id == srvg.timelinePhases.betstop.id // srvg.timelinePhases.sportevent.id
        ) {
            return true;
        }
        return false;
    }

    /**
     * if not on autopilot, but selected matchday is in the past, turn autopilot on
     * if on autopilot, set the matchday picker range and position accordingly
     * if not on autopilot, make the matchday picker range bigger if necessary
     */
    private updateMatchdaypickerSelectedMatchday( currentMatchday: Matchday, currentPhase: Phase ) {
        // if selected matchday is in a season previous to the current matchday, clear range and turn autopilot on if off
        var range = this.matchdayArea.matchdayPicker.range.getMatchdays();
        if( range && range[0] ) {
            if ( range[0].getSeason() == null ||
                this.matchdayArea.matchdayAreaModel.currentMatchday &&
                range[0].getSeason().nr < this.matchdayArea.matchdayAreaModel.currentMatchday.getSeason().nr

            ) {
                if( ! this.isOnAutopilot ) {
                    this.isOnAutopilot = true;
                }
                this.matchdaypickerController.updateRange(currentMatchday.getSeason(), currentMatchday);
                this.matchdayArea.matchdayPicker.setMatchday(currentMatchday);
            }
        }

        if (this.isOnAutopilot) {
            if (!this.matchdaypickerController.isRangeValid(currentMatchday.getSeason(), currentMatchday)) {
                // range is invalid or no longer current, update it
                this.matchdaypickerController.updateRange(currentMatchday.getSeason(), currentMatchday);
            }
        }
        else {
            // If the timeline changes to a newer season, the picker range should be adjusted
            var maxMatchday = this.matchdayArea.matchdayPicker.range.length
                ? this.matchdayArea.matchdayPicker.range[this.matchdayArea.matchdayPicker.range.length - 1]
                : null;
            if (
                !maxMatchday
                || maxMatchday.getSeason().id < currentMatchday.getSeason().id
                || ( maxMatchday.getSeason().id == currentMatchday.getSeason().id && maxMatchday.nr <= currentMatchday.nr )
            ) {
                this.matchdaypickerController.extendRangeToSeason(currentMatchday.getSeason());
            }
        }
        this.matchdaypickerController.updateHomeButton();

        this.matchdaypickerController.refreshView();
    }

    // ************************** Event handlers **************************

    handleMatchdayPickEvent(event:MatchdayPickEvent, provider) {
        if( provider == this.matchdaypickerController ) {
            // matchdaypicker was clicked
            if( ! event.matchday.isEqualTo( this.matchdayArea.matchdayAreaModel.selectedMatchday ) ) {
                this.setSelectedMatchday( event.matchday, 'clicked' );
            }
            this.isOnAutopilot = false;
        }
        else if( provider == app.customerIntegration ) {
            // customer-facing API was called
            // select the matchday in the matchdaypicker, which will act like it was clicked
            this.setSelectedMatchday( event.matchday, 'callback' );
            //this.matchdayArea.matchdayPicker.setMatchday(event.matchday);
            this.matchdayArea.matchdayPicker.selectIndex( this.matchdayArea.matchdayPicker.selectedIndex );
        }
    }

    handleDataReceiveEvent( event:DataReceiveEvent, provider ) {
        if( provider == app.matchIdsFeedController ) {
            if( this.oddsFrameViewController &&
                event.query.season_id == this.matchdayArea.matchdayAreaModel.selectedMatchday.getSeason().id &&
                event.query.matchday_nr == this.matchdayArea.matchdayAreaModel.selectedMatchday.nr
            ) {
                this.oddsFrameViewController.handleDataReceiveEvent( event, provider );
            }
        }
        else if( provider == app.oddsfeedController ){
            if( this.oddsViewController ) {
                this.oddsViewController.handleDataReceiveEvent( event, provider );
            }
        }
    }
}